/**
 * KsefSettingsTab — konfiguracja połączeń KSeF per firma (Faza 1).
 *
 * Lista połączeń + formularz (firma, NIP, środowisko, token/certyfikat) + „Testuj połączenie".
 * Backend: /api/ksef/connections (+ [id], [id]/test). Sekrety szyfrowane pgcrypto po stronie API.
 * Autoryzacja: token KSeF lub certyfikat kwalifikowany (podpis XAdES, Faza 4).
 *
 * Teksty PL inline (konwencja gałęzi roboczej — ekstrakcja i18n przed docelowym mergem).
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { CalendarClock, FileCheck, PlugZap, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { KsefAutomationRulesSection } from '@/components/settings/KsefAutomationRulesSection';
import { KsefPermissionsSection } from '@/components/settings/KsefPermissionsSection';
import {
  type KsefSyncConfig,
  KsefSyncScheduleEditor,
} from '@/components/settings/KsefSyncScheduleEditor';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface KsefConnectionDto {
  id: string;
  companyId: string;
  nip: string;
  companyName: string | null;
  environment: string;
  authType: string;
  status: string;
  lastSyncMessage: string | null;
  isActive: boolean;
  authTokenSet: boolean;
  certificateSet: boolean;
  syncConfig: KsefSyncConfig | null;
}

const KEY = ['ksef-connections'] as const;

const ENV_LABELS: Record<string, string> = {
  test: 'Testowe',
  demo: 'Demo',
  prod: 'Produkcyjne',
};

const STATUS_META: Record<string, { label: string; cls: string }> = {
  connected: { label: 'Połączono', cls: 'bg-success-100 text-success-700 dark:bg-success-900/30' },
  error: { label: 'Błąd', cls: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30' },
  disconnected: { label: 'Niepołączono', cls: 'bg-bg-tertiary text-text-secondary' },
};

const inputCls =
  'w-full rounded-md border border-border-primary bg-bg-primary px-2.5 py-1.5 text-sm text-text-primary';
const labelCls = 'mb-1 block text-xs font-medium text-text-secondary';

interface FormState {
  companyId: string;
  nip: string;
  companyName: string;
  environment: 'test' | 'demo' | 'prod';
  authType: 'token' | 'certificate';
  authToken: string;
  certificatePem: string;
  certificateKeyPem: string;
}

function emptyForm(companyId: string): FormState {
  return {
    companyId,
    nip: '',
    companyName: '',
    environment: 'test',
    authType: 'token',
    authToken: '',
    certificatePem: '',
    certificateKeyPem: '',
  };
}

export function KsefSettingsTab() {
  const toast = useToast();
  const qc = useQueryClient();
  const { me } = useTenantAuth();
  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);

  const [form, setForm] = useState<FormState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<KsefConnectionDto | null>(null);
  const [scheduleOpenId, setScheduleOpenId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const res = await api.get<{ connections: KsefConnectionDto[] }>('/ksef/connections');
      return res.data.connections ?? [];
    },
  });
  const connections = listQuery.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMutation = useMutation({
    mutationFn: async (f: FormState) => {
      const body: Record<string, unknown> = {
        companyId: f.companyId,
        nip: f.nip.trim(),
        companyName: f.companyName.trim() || null,
        environment: f.environment,
        authType: f.authType,
      };
      if (f.authType === 'token') {
        if (f.authToken.trim()) body.authToken = f.authToken.trim();
      } else {
        if (f.certificatePem.trim()) body.certificatePem = f.certificatePem.trim();
        if (f.certificateKeyPem.trim()) body.certificateKeyPem = f.certificateKeyPem.trim();
      }
      return api.post('/ksef/connections', body);
    },
    onSuccess: () => {
      toast.show({ title: 'Dodano połączenie KSeF', variant: 'success' });
      setForm(null);
      invalidate();
    },
    onError: (err) => {
      const code = isAxiosError(err) ? err.response?.data?.error : undefined;
      toast.show({
        title:
          code === 'KSEF_CONNECTION_EXISTS'
            ? 'Połączenie dla tego NIP i środowiska już istnieje w tej firmie.'
            : 'Nie udało się dodać połączenia.',
        variant: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/ksef/connections/${id}`),
    onSuccess: () => {
      toast.show({ title: 'Usunięto połączenie', variant: 'success' });
      setPendingDelete(null);
      invalidate();
    },
    onError: () => toast.show({ title: 'Nie udało się usunąć.', variant: 'error' }),
  });

  const testMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ ok: boolean; message: string }>(
        `/ksef/connections/${id}/test`,
        {},
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.show({ title: data.message, variant: data.ok ? 'success' : 'error' });
      invalidate();
    },
    onError: (err) => {
      const code = isAxiosError(err) ? err.response?.data?.error : undefined;
      const msg =
        code === 'KSEF_TOKEN_NOT_SET'
          ? 'Brak skonfigurowanego tokenu KSeF dla tego połączenia.'
          : code === 'KSEF_CERTIFICATE_NOT_SET'
            ? 'Brak certyfikatu dla tego połączenia.'
            : 'Test połączenia nie powiódł się.';
      toast.show({ title: msg, variant: 'error' });
    },
  });

  const canSubmit =
    form &&
    form.companyId &&
    /^\d{10}$/.test(form.nip.trim()) &&
    (form.authType === 'token' ? form.authToken.trim().length > 0 : true);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-text-primary">
          <FileCheck className="h-5 w-5 text-accent-600" />
          Integracja KSeF
        </h2>
        <p className="text-sm text-text-secondary">
          Połączenia z Krajowym Systemem e-Faktur — osobno dla każdej firmy (NIP). Sekrety (token /
          certyfikat) są szyfrowane i nigdy nie opuszczają serwera.
        </p>
      </div>

      {companies.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border-primary p-4 text-sm text-text-muted">
          Brak firm w tym tenancie — najpierw dodaj firmę, aby skonfigurować KSeF.
        </div>
      ) : (
        <>
          {!form && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setForm(emptyForm(companies[0]!.id))}
            >
              Dodaj połączenie
            </Button>
          )}

          {form && (
            <div className="space-y-3 rounded-xl border border-border-primary bg-bg-elevated p-4">
              <h3 className="text-base font-semibold text-text-primary">Nowe połączenie KSeF</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Firma</label>
                  <Dropdown
                    value={form.companyId}
                    onChange={(val) => setForm({ ...form, companyId: val })}
                    options={companies.map((c) => ({ value: c.id, label: c.name }))}
                    aria-label="Firma"
                  />
                </div>
                <div>
                  <label className={labelCls}>NIP (10 cyfr)</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="1234567890"
                    value={form.nip}
                    onChange={(e) =>
                      setForm({ ...form, nip: e.target.value.replace(/\D/g, '').slice(0, 10) })
                    }
                  />
                </div>
                <div>
                  <label className={labelCls}>Środowisko</label>
                  <Dropdown
                    value={form.environment}
                    onChange={(val) =>
                      setForm({ ...form, environment: val as FormState['environment'] })
                    }
                    options={[
                      { value: 'test', label: 'Testowe' },
                      { value: 'demo', label: 'Demo' },
                      { value: 'prod', label: 'Produkcyjne' },
                    ]}
                    aria-label="Środowisko"
                  />
                </div>
                <div>
                  <label className={labelCls}>Sposób autoryzacji</label>
                  <Dropdown
                    value={form.authType}
                    onChange={(val) => setForm({ ...form, authType: val as FormState['authType'] })}
                    options={[
                      { value: 'token', label: 'Token KSeF' },
                      { value: 'certificate', label: 'Certyfikat kwalifikowany' },
                    ]}
                    aria-label="Sposób autoryzacji"
                  />
                </div>
              </div>

              {form.authType === 'token' ? (
                <div>
                  <label className={labelCls}>Token KSeF</label>
                  <Input
                    type="password"
                    placeholder="Token wygenerowany w KSeF"
                    value={form.authToken}
                    onChange={(e) => setForm({ ...form, authToken: e.target.value })}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <label className={labelCls}>Certyfikat (PEM)</label>
                    <textarea
                      rows={3}
                      placeholder="-----BEGIN CERTIFICATE-----"
                      value={form.certificatePem}
                      onChange={(e) => setForm({ ...form, certificatePem: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Klucz prywatny (PEM)</label>
                    <textarea
                      rows={3}
                      placeholder="-----BEGIN PRIVATE KEY-----"
                      value={form.certificateKeyPem}
                      onChange={(e) => setForm({ ...form, certificateKeyPem: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <p className="text-xs text-text-tertiary">
                    Dokument logowania podpisujemy XAdES-em Twoim certyfikatem kwalifikowanym.
                    Certyfikat i klucz prywatny w formacie PEM — nigdy nie opuszczają serwera.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 border-t border-border-primary pt-3">
                <Button variant="secondary" size="sm" onClick={() => setForm(null)}>
                  Anuluj
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canSubmit || createMutation.isPending}
                  loading={createMutation.isPending}
                  onClick={() => form && createMutation.mutate(form)}
                >
                  Zapisz
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {listQuery.isLoading && <p className="text-sm text-text-muted">Ładowanie…</p>}
            {!listQuery.isLoading && connections.length === 0 && (
              <div className="rounded-lg border border-dashed border-border-primary p-4 text-center text-sm text-text-muted">
                Brak połączeń KSeF. Dodaj pierwsze dla wybranej firmy.
              </div>
            )}
            {connections.map((c) => {
              const status = STATUS_META[c.status] ?? STATUS_META.disconnected!;
              const companyLabel =
                companies.find((co) => co.id === c.companyId)?.name ?? c.companyName ?? '—';
              const autoOn = c.syncConfig?.auto_sync_enabled === true;
              return (
                <div key={c.id} className="rounded-lg border border-border-primary p-3">
                  <div className="flex items-center gap-3">
                    <FileCheck className="h-5 w-5 shrink-0 text-accent-600" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-text-primary">
                        {companyLabel} · NIP {c.nip}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-text-muted">
                        <span>{ENV_LABELS[c.environment] ?? c.environment}</span>
                        <span>·</span>
                        <span>{c.authType === 'token' ? 'Token' : 'Certyfikat'}</span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${status.cls}`}
                        >
                          {status.label}
                        </span>
                        {autoOn && (
                          <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[11px] font-medium text-accent-700 dark:bg-accent-900/30">
                            Auto-sync
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant={scheduleOpenId === c.id ? 'primary' : 'secondary'}
                      size="sm"
                      leftIcon={<CalendarClock className="h-4 w-4" />}
                      onClick={() => setScheduleOpenId((prev) => (prev === c.id ? null : c.id))}
                    >
                      Harmonogram
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<PlugZap className="h-4 w-4" />}
                      loading={testMutation.isPending && testMutation.variables === c.id}
                      onClick={() => testMutation.mutate(c.id)}
                    >
                      Testuj
                    </Button>
                    <button
                      type="button"
                      title="Usuń"
                      className="shrink-0 rounded p-1.5 text-danger-600 hover:bg-bg-tertiary"
                      onClick={() => setPendingDelete(c)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {scheduleOpenId === c.id && (
                    <KsefSyncScheduleEditor connectionId={c.id} syncConfig={c.syncConfig} />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Usunąć połączenie KSeF?"
        message={
          pendingDelete ? `Czy na pewno usunąć połączenie KSeF dla NIP ${pendingDelete.nip}?` : ''
        }
        confirmLabel="Usuń"
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />

      {companies.length > 0 && <KsefAutomationRulesSection />}
      {companies.length > 0 && <KsefPermissionsSection />}
    </div>
  );
}

export default KsefSettingsTab;
